import { Check, ChevronDown, Plus } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface SuggestedInputProps {
  id: string
  label: string
  value: string
  suggestions: string[]
  placeholder: string
  disabled: boolean
  describedBy?: string
  onValueChange: (value: string) => void
}

export function SuggestedInput({
  id,
  label,
  value,
  suggestions,
  placeholder,
  disabled,
  describedBy,
  onValueChange,
}: SuggestedInputProps) {
  const optionsId = `${id}-options`
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const query = value.trim().toLocaleLowerCase()
  const filteredSuggestions = useMemo(
    () =>
      suggestions.filter((suggestion) =>
        suggestion.toLocaleLowerCase().includes(query)
      ),
    [query, suggestions]
  )
  const hasExactMatch = suggestions.some(
    (suggestion) => suggestion.toLocaleLowerCase() === query
  )
  const choices = [
    ...(value.trim() && !hasExactMatch
      ? [{ value: value.trim(), create: true }]
      : []),
    ...filteredSuggestions.map((suggestion) => ({
      value: suggestion,
      create: false,
    })),
  ]

  function selectValue(nextValue: string) {
    onValueChange(nextValue)
    setActiveIndex(-1)
    setOpen(false)
    inputRef.current?.focus()
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              id={id}
              role="combobox"
              aria-expanded={open}
              aria-controls={optionsId}
              aria-autocomplete="list"
              aria-activedescendant={
                open && activeIndex >= 0
                  ? `${optionsId}-${activeIndex}`
                  : undefined
              }
              value={value}
              onChange={(event) => {
                onValueChange(event.target.value)
                setActiveIndex(-1)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault()
                  setOpen(true)
                  setActiveIndex((current) =>
                    Math.min(current + 1, choices.length - 1)
                  )
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault()
                  setOpen(true)
                  setActiveIndex((current) =>
                    current <= 0 ? choices.length - 1 : current - 1
                  )
                } else if (event.key === 'Enter' && open && activeIndex >= 0) {
                  event.preventDefault()
                  selectValue(choices[activeIndex].value)
                } else if (event.key === 'Escape') {
                  setOpen(false)
                  setActiveIndex(-1)
                }
              }}
              placeholder={placeholder}
              aria-describedby={describedBy}
              disabled={disabled}
              className="pr-10"
            />
            <button
              type="button"
              aria-label={`Show ${label} options`}
              className="absolute inset-y-0 right-0 flex w-10 cursor-pointer items-center justify-center text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                inputRef.current?.focus()
                setOpen(true)
              }}
            >
              <ChevronDown
                className={cn(
                  'size-4 transition-transform duration-150 motion-reduce:transition-none',
                  open && 'rotate-180'
                )}
              />
            </button>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-56 p-1"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div
            id={optionsId}
            role="listbox"
            aria-label={`${label} options`}
            className="max-h-60 overflow-y-auto"
          >
            {choices.length > 0 ? (
              choices.map((choice, index) => (
                <button
                  key={`${choice.create ? 'create' : 'existing'}-${choice.value}`}
                  id={`${optionsId}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={
                    !choice.create && choice.value.toLocaleLowerCase() === query
                  }
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm outline-none',
                    activeIndex === index
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  )}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => selectValue(choice.value)}
                >
                  {choice.create ? (
                    <Plus className="size-4 text-primary" />
                  ) : (
                    <Check
                      className={cn(
                        'size-4',
                        choice.value.toLocaleLowerCase() === query
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                  )}
                  <span className="truncate">
                    {choice.create ? `Use “${choice.value}”` : choice.value}
                  </span>
                </button>
              ))
            ) : (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                No previous values yet. Type to add one.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
